const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);

export const EMULATOR_PROJECT_ID = "demo-lettucetalk";
export const AUTH_EMULATOR_URL = "http://127.0.0.1:9099";
export const FIRESTORE_EMULATOR_HOST = "127.0.0.1";
export const FIRESTORE_EMULATOR_PORT = 8080;
export const FUNCTIONS_EMULATOR_BASE_URL =
  "http://127.0.0.1:5001/demo-lettucetalk/us-central1";

export const ACTIVE_MCAAD_FUNCTION_NAMES = Object.freeze([
  "createLettuceTalkCards",
  "getPublicWallCardsForWall",
  "publishLettuceTalkCardToWall",
  "shareLettuceTalkCard",
  "recordLettuceTalkCardShare",
  "getYourImpact",
  "createLettuceTalkMergeTicket",
  "redeemLettuceTalkMergeTicket",
  "updateLettuceTalkCardText",
]);

const DERIVABLE_FUNCTION_NAMES = ACTIVE_MCAAD_FUNCTION_NAMES.join("|");

function getCurrentHostname() {
  if (typeof window !== "undefined" && window.location) {
    return window.location.hostname;
  }

  return "";
}

export function isLoopbackHostname(hostname) {
  return LOOPBACK_HOSTNAMES.has(
    typeof hostname === "string" ? hostname.trim().toLowerCase() : ""
  );
}

export function getFirebaseRuntime({
  env = process.env,
  hostname = getCurrentHostname(),
} = {}) {
  const emulatorFlagEnabled =
    env.REACT_APP_USE_FIREBASE_EMULATORS === "true";

  if (!emulatorFlagEnabled) {
    return { useEmulators: false };
  }

  if (env.NODE_ENV !== "development") {
    const error = new Error(
      "Firebase emulator mode requires a development build."
    );
    error.code = "firebase-emulator-development-only";
    throw error;
  }

  if (!isLoopbackHostname(hostname)) {
    const error = new Error(
      "Firebase emulator mode requires a loopback hostname such as localhost."
    );
    error.code = "firebase-emulator-loopback-only";
    throw error;
  }

  return { useEmulators: true };
}

function deriveFunctionUrl(url, functionName) {
  if (typeof url !== "string" || !url || !functionName) {
    return "";
  }

  const suffixPattern = new RegExp(`(?:${DERIVABLE_FUNCTION_NAMES})$`);
  return suffixPattern.test(url) ? url.replace(suffixPattern, functionName) : "";
}

function isValidHttpUrl(url) {
  if (typeof url !== "string" || !url.trim()) {
    return false;
  }

  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
}

function throwInvalidLocalFunctionUrl(functionName) {
  const error = new Error(
    `Missing or invalid local URL configuration for ${functionName}.`
  );
  error.code = "invalid-local-function-url";
  throw error;
}

function resolveLocalFunctionUrl(directUrl, fallbackUrls, functionName) {
  if (typeof directUrl === "string" && directUrl.trim()) {
    if (isValidHttpUrl(directUrl)) {
      return directUrl;
    }

    throwInvalidLocalFunctionUrl(functionName);
  }

  for (const url of fallbackUrls) {
    const derivedUrl = deriveFunctionUrl(url, functionName);

    if (isValidHttpUrl(derivedUrl)) {
      return derivedUrl;
    }
  }

  throwInvalidLocalFunctionUrl(functionName);
}

/**
 * Resolve an active MCAAD/refine HTTP function. Explicit local modes fail
 * closed when their local URL configuration is absent or invalid.
 */
export function resolveActiveFunctionUrl(
  functionName,
  {
    devUrl = "",
    prodUrl = "",
    devFallbackUrls = [],
    prodFallbackUrls = [],
    env = process.env,
    hostname,
  } = {}
) {
  if (!ACTIVE_MCAAD_FUNCTION_NAMES.includes(functionName)) {
    throw new Error("Unknown active MCAAD function");
  }

  const { useEmulators } = getFirebaseRuntime({ env, hostname });

  if (useEmulators) {
    return `${FUNCTIONS_EMULATOR_BASE_URL}/${functionName}`;
  }

  const useLegacyLocalIntent =
    env.REACT_APP_USE_LOCAL_CREATE_CARDS === "true";

  if (useLegacyLocalIntent) {
    if (env.NODE_ENV !== "development") {
      const error = new Error(
        "Legacy local Firebase Functions mode requires a development build."
      );
      error.code = "legacy-local-functions-development-only";
      throw error;
    }

    return resolveLocalFunctionUrl(devUrl, devFallbackUrls, functionName);
  }

  if (prodUrl) {
    return prodUrl;
  }

  for (const url of prodFallbackUrls) {
    const derivedUrl = deriveFunctionUrl(url, functionName);

    if (derivedUrl) {
      return derivedUrl;
    }
  }

  return "";
}
